import { computed, ref } from "vue";
import { useSlugInfo } from "@/shared/common";
import type { ICertificateInfoBlock } from "@/shared/thorlabs/support-documents/types";
import type { ComputedRef, Ref } from "vue";

interface IUseCertificateInfoBlockReturn {
  certificateInfoBlock: ComputedRef<ICertificateInfoBlock>;
  load: () => Promise<void>;
  isLoaded: Ref<boolean>;
}

export function useCertificateInfoBlock(): IUseCertificateInfoBlockReturn {
  // Note: useSlugInfo is called here, which triggers the slug GraphQL query
  // This is unavoidable in Vue composables, but it only happens when the component mounts
  // With v-if on the dialog components, this won't happen until the user clicks
  const { hasContent, contentResult, fetchContent, loading } = useSlugInfo("CertificateInfo");
  const isLoaded = ref(false);

  const certificateInfoBlock = computed<ICertificateInfoBlock>(() => {
    try {
      const certificateInfoData: ICertificateInfoBlock = JSON.parse(
        contentResult?.value?.page?.content ?? "{}",
      ) as ICertificateInfoBlock;
      if (certificateInfoData) {
        return certificateInfoData as ICertificateInfoBlock;
      }
    } catch (error) {
      console.error("Failed to parse CertificateInfoBlock data:", error);
    }

    return {} as ICertificateInfoBlock;
  });

  const load = async () => {
    if (isLoaded.value) {
      return;
    }

    // Wait for slug info to load if it's still loading
    while (loading.value) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    if (hasContent.value) {
      await fetchContent();
      isLoaded.value = true;
    }
  };

  return {
    certificateInfoBlock,
    load,
    isLoaded,
  };
}
